import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BookOpen, ClipboardText, MagnifyingGlass, Calendar, ArrowRight, Scales,
  FileText, Target, CheckCircle, Clock, Scales as Balance, Warning, Lightbulb,
  GraduationCap, FlowArrow,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Fluxo da fase preparatória da Lei 14.133/2021:
 *
 *     ① DFD → ② ETP → ③ Pesquisa → ④ TR/PB → ⑤ Matriz de Riscos
 *     ⇅                                                       ⇅
 *                           PCA (consolida tudo)
 *
 * - Em azul (bandeira): etapas já implementadas no sistema
 * - Em amarelo: ainda a implementar / em breve
 */

const ETAPAS = [
  {
    id: "dfd",
    numero: "1",
    titulo: "DFD — Documento de Formalização da Demanda",
    base: "Lei 14.133 · art. 6º, XIX e art. 72, I",
    descricao: "Formaliza a necessidade da contratação pela unidade requisitante, descrevendo o objeto, a justificativa, o valor estimado e o prazo desejado.",
    icon: ClipboardText,
    status: "ativo",
    link: "/demandas",
    cta: "Abrir Demandas",
    accent: "border-[#009C3B]/40 text-[#12B850] bg-[#009C3B]/10",
  },
  {
    id: "etp",
    numero: "2",
    titulo: "ETP — Estudo Técnico Preliminar",
    base: "Lei 14.133 · art. 18, §1º",
    descricao: "Documento técnico que descreve a análise da viabilidade, o levantamento de mercado, as alternativas, os benefícios esperados e a adequação orçamentária.",
    icon: Lightbulb,
    status: "ativo",
    link: "/etps",
    cta: "Abrir ETPs",
    accent: "border-[#009C3B]/40 text-[#12B850] bg-[#009C3B]/10",
  },
  {
    id: "pesquisa",
    numero: "3",
    titulo: "Pesquisa de Preços",
    base: "Lei 14.133 · art. 23 · IN SEGES/ME 65/2021",
    descricao: "Levantamento de valores de referência via PNCP, Comprasnet, atas, contratos similares e cotações, com cálculo da média/mediana para o preço estimado.",
    icon: MagnifyingGlass,
    status: "ativo",
    link: "/pesquisas",
    cta: "Abrir Pesquisas",
    accent: "border-[#009C3B]/40 text-[#12B850] bg-[#009C3B]/10",
  },
  {
    id: "tr",
    numero: "4",
    titulo: "TR / PB — Termo de Referência / Projeto Básico",
    base: "Lei 14.133 · art. 6º, XXIII e art. 40, §1º",
    descricao: "Descrição completa do objeto, especificações, modelo de execução, obrigações contratuais, critérios de medição e critérios de julgamento.",
    icon: GraduationCap,
    status: "ativo",
    link: "/trs",
    cta: "Abrir TRs",
    accent: "border-[#009C3B]/40 text-[#12B850] bg-[#009C3B]/10",
  },
  {
    id: "matriz",
    numero: "5",
    titulo: "Matriz de Riscos",
    base: "Lei 14.133 · art. 22 e art. 103",
    descricao: "Identificação dos riscos relevantes, probabilidade, impacto, responsável pela gestão e medidas de mitigação durante a execução contratual.",
    icon: Warning,
    status: "ativo",
    link: "/matrizes-riscos",
    cta: "Abrir Matrizes",
    accent: "border-[#009C3B]/40 text-[#12B850] bg-[#009C3B]/10",
  },
  {
    id: "pca",
    numero: "6",
    titulo: "PCA — Plano de Contratações Anual",
    base: "Lei 14.133 · art. 12, VII · Decreto 10.947/2022",
    descricao: "Consolidação anual de todas as contratações programadas, com valores globais, cronograma, categorias e vínculo às contratações do exercício.",
    icon: Calendar,
    status: "ativo",
    link: "/pac",
    cta: "Abrir PCA",
    accent: "border-[#009C3B]/40 text-[#12B850] bg-[#009C3B]/10",
  },
];

export default function Planejamento() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ dfd: null, pesquisas: null, pca: null });

  useEffect(() => {
    const muni = user?.role !== "admin" && user?.municipio_id ? `?municipio_id=${user.municipio_id}` : "";
    Promise.all([
      api.get(`/demandas/stats${muni}`).then((r) => r.data).catch(() => null),
      api.get(`/pesquisas`).then((r) => r.data).catch(() => null),
      api.get(`/pac?ano=${new Date().getFullYear()}&limit=1`).then((r) => r.data).catch(() => null),
    ]).then(([dfd, pesquisas, pca]) => setStats({ dfd, pesquisas, pca }));
  }, [user]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="uppercase-label flex items-center gap-1.5">
          <BookOpen size={12} weight="duotone" className="text-[#009C3B]" /> Fase preparatória
        </div>
        <h1 className="font-heading text-4xl mt-1">Planejamento da Contratação</h1>
        <p className="text-sm text-[#94A3B8] mt-1 max-w-3xl">
          Visão unificada dos artefatos exigidos pela <b>Lei nº 14.133/2021</b> para a fase preparatória
          das contratações municipais. Cada etapa pode ser gerida de forma independente, com rastreabilidade
          completa e vínculo automático com o Plano de Contratações Anual (PCA).
        </p>
      </div>

      {/* Faixa das 3 cores da bandeira do Brasil (institucional) */}
      <div className="h-1 w-full br-faixa-bandeira rounded mb-6" />

      {/* Fluxo integrado dos processos */}
      <FluxoIntegrado />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-8">
        <KPI
          label="Demandas (DFD)"
          valor={stats.dfd?.total?.qtd ?? "—"}
          sub={stats.dfd?.aprovada ? `${stats.dfd.aprovada.qtd} aprovada(s)` : "no município"}
          icon={ClipboardText}
          tone="verde"
        />
        <KPI
          label="Pesquisas"
          valor={stats.pesquisas?.total ?? "—"}
          sub="em elaboração / concluídas"
          icon={MagnifyingGlass}
          tone="verde"
        />
        <KPI
          label="Itens no PCA"
          valor={stats.pca?.total ?? "—"}
          sub={`Exercício ${new Date().getFullYear()}`}
          icon={Calendar}
          tone="verde"
        />
        <KPI
          label="Base legal"
          valor="14.133"
          sub="Lei de Licitações 2021"
          icon={Scales}
          tone="azul"
        />
      </div>

      {/* Linha do tempo das etapas */}
      <div className="mb-4">
        <div className="uppercase-label">Fluxo da fase preparatória</div>
        <h2 className="font-heading text-xl mt-1 mb-4">6 etapas · Lei 14.133/2021</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ETAPAS.map((e, i) => <EtapaCard key={e.id} e={e} index={i} />)}
      </div>

      {/* Rodapé explicativo */}
      <div className="mt-8 bg-[#1E293B] border border-[#334155] rounded-lg p-6">
        <div className="flex items-start gap-3">
          <BookOpen size={22} className="text-[#FFDF00] mt-1" weight="duotone" />
          <div>
            <h3 className="font-heading text-lg mb-2">Sobre a Lei 14.133/2021</h3>
            <p className="text-sm text-[#94A3B8] leading-relaxed">
              A <b className="text-[#E9EEF5]">Nova Lei de Licitações e Contratos Administrativos</b> instituiu o
              <b className="text-[#E9EEF5]"> planejamento como princípio</b> das contratações públicas (art. 5º).
              A fase preparatória, regida pelos artigos 18 a 29, exige que toda contratação seja precedida de estudos
              formais que demonstrem a <i>viabilidade técnica, econômica e orçamentária</i>, bem como a adequação ao
              <b className="text-[#E9EEF5]"> Plano de Contratações Anual</b> do órgão (art. 12, VII).
            </p>
            <p className="text-sm text-[#94A3B8] leading-relaxed mt-2">
              Este módulo centraliza os artefatos obrigatórios para que o gestor municipal cumpra integralmente
              o devido planejamento das contratações do exercício, com transparência ativa (CF art. 37, caput)
              e aderência às orientações do <b className="text-[#E9EEF5]">Tribunal de Contas do Estado do Ceará</b>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, valor, sub, icon: Icon, tone }) {
  const tones = {
    verde: "border-[#009C3B]/40 text-[#12B850]",
    azul: "border-[#002776]/50 text-[#3B82F6]",
    amarelo: "border-[#FFDF00]/40 text-[#FFDF00]",
  };
  return (
    <div className={`tce-card p-4 border ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="uppercase-label text-[10px]">{label}</div>
          <div className="font-heading text-3xl mt-1">{valor}</div>
          <div className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</div>
        </div>
        <Icon size={22} weight="duotone" />
      </div>
    </div>
  );
}

function EtapaCard({ e, index }) {
  const Icon = e.icon;
  const ativo = e.status === "ativo";
  return (
    <div className={`tce-card p-5 border-2 ${e.accent} relative overflow-hidden`}
         data-testid={`etapa-${e.id}`}>
      <div className="absolute top-2 right-3 text-6xl font-heading opacity-[0.07] select-none pointer-events-none">
        {e.numero}
      </div>
      <div className="relative flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${e.accent}`}>
            <Icon size={20} weight="duotone" />
          </div>
          <div>
            <div className="uppercase-label text-[10px]">Etapa {e.numero}</div>
            <h3 className="font-heading text-lg mt-0.5 leading-tight">{e.titulo}</h3>
            <div className="text-[10px] text-[#64748B] mt-1 font-mono">{e.base}</div>
          </div>
        </div>
        {ativo ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#009C3B]/15 text-[#12B850] border border-[#009C3B]/40 text-[10px] font-medium flex-shrink-0">
            <CheckCircle size={10} weight="fill" /> Ativo
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFDF00]/10 text-[#FFDF00] border border-[#FFDF00]/40 text-[10px] font-medium flex-shrink-0">
            <Clock size={10} weight="fill" /> Em breve
          </span>
        )}
      </div>
      <p className="relative text-sm text-[#CBD5E1] leading-relaxed mb-4">{e.descricao}</p>
      {ativo && e.link ? (
        <Link to={e.link} data-testid={`etapa-cta-${e.id}`}
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#009C3B] hover:bg-[#12B850] text-white text-xs font-semibold transition-colors">
          {e.cta} <ArrowRight size={12} weight="bold" />
        </Link>
      ) : (
        <div className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#FFDF00]/30 text-[#FFDF00] text-xs font-medium cursor-not-allowed">
          <Clock size={11} /> {e.cta}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Fluxo Integrado — mostra a cadeia DFD→ETP→TR→Matriz→PCA por processo
// ============================================================

const ESTAGIO_META = {
  dfd_pendente: { label: "DFD em elaboração", color: "#94A3B8", pct: 10 },
  dfd_ok: { label: "DFD aprovada — pronta p/ ETP", color: "#009C3B", pct: 25 },
  etp_ok: { label: "ETP aprovado — pronto p/ TR", color: "#12B850", pct: 45 },
  tr_ok: { label: "TR aprovado — pronto p/ Matriz", color: "#FFDF00", pct: 70 },
  matriz_ok: { label: "Matriz aprovada — pronta p/ PCA", color: "#002776", pct: 90 },
  no_pca: { label: "Integrado ao PCA", color: "#1E4FBF", pct: 100 },
};

function FluxoIntegrado() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [processos, setProcessos] = useState([]);
  const [filtroEstagio, setFiltroEstagio] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filtroEstagio) p.set("status", filtroEstagio);
    p.set("limit", "50");
    api.get(`/planejamento/processos?${p}`)
      .then((r) => setProcessos(r.data?.items || []))
      .catch(() => setProcessos([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroEstagio]);

  const promover = async (tipo, origem_id, origem_num) => {
    if (!window.confirm(`Promover ${origem_num} para a próxima etapa?`)) return;
    try {
      const r = await api.post(`/${tipo}/${origem_id}/promover-${tipo === "demandas" ? "etp" : tipo === "etps" ? "tr" : "matriz"}`, {});
      toast.success(r.data?.numero_gerado ? `${r.data.numero_gerado} criado com modelo padrão Lei 14.133 + TCU` : "Promovido");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao promover"); }
  };

  return (
    <div className="tce-card p-5 mb-6" data-testid="fluxo-integrado">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md border border-[#002776]/40 bg-[#002776]/10 flex items-center justify-center">
            <FlowArrow size={20} weight="duotone" className="text-[#3B82F6]" />
          </div>
          <div>
            <div className="uppercase-label flex items-center gap-1.5">
              <CheckCircle size={10} weight="duotone" className="text-[#009C3B]" /> Fluxo Integrado
            </div>
            <h2 className="font-heading text-xl mt-0.5">Processos de contratação</h2>
            <p className="text-xs text-[#94A3B8] mt-0.5">
              Encadeamento DFD → ETP → TR → Matriz → PCA · modelo padronizado Lei 14.133 + TCU aplicado automaticamente
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={filtroEstagio} onChange={(e) => setFiltroEstagio(e.target.value)}
                  className="tce-input px-3 py-2 rounded-md text-xs" data-testid="fluxo-filtro">
            <option value="">Todos os estágios</option>
            {Object.entries(ESTAGIO_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button onClick={() => setExpanded((s) => !s)} data-testid="fluxo-toggle"
                  className="px-3 py-2 rounded-md border border-[#334155] text-xs text-[#94A3B8] hover:border-[#FFDF00]">
            {expanded ? "Recolher" : "Expandir"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-2.5">
          {loading && <div className="text-center text-xs text-[#94A3B8] py-6">Carregando processos…</div>}
          {!loading && processos.length === 0 && (
            <div className="text-center text-xs text-[#94A3B8] py-8 border border-dashed border-[#334155] rounded">
              Nenhum processo encontrado{filtroEstagio ? " para o filtro selecionado" : ""}.
              <br />Comece criando uma <Link to="/demandas" className="text-[#FFDF00] underline">Demanda (DFD)</Link>.
            </div>
          )}
          {!loading && processos.map((p) => (
            <ProcessoCard key={p.demanda.id} proc={p} onPromover={promover} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProcessoCard({ proc, onPromover }) {
  const estagio = ESTAGIO_META[proc.estagio] || ESTAGIO_META.dfd_pendente;
  const steps = [
    { k: "dfd", label: "DFD", data: proc.demanda, link: "/demandas" },
    { k: "etp", label: "ETP", data: proc.etp, link: "/etps" },
    { k: "tr", label: "TR", data: proc.tr, link: "/trs" },
    { k: "matriz", label: "Matriz", data: proc.matriz, link: "/matrizes-riscos" },
    { k: "pca", label: "PCA", data: proc.pac_item && { numero_gerado: `#${String(proc.pac_item.numero_seq).padStart(4,"0")}`, status: "planejado" }, link: "/pac" },
  ];

  // Determina a próxima ação possível
  let nextAction = null;
  if (!proc.etp && proc.demanda.status === "aprovada")
    nextAction = { tipo: "demandas", id: proc.demanda.id, num: proc.demanda.numero_gerado, label: "Gerar ETP" };
  else if (proc.etp && !proc.tr && proc.etp.status === "aprovado")
    nextAction = { tipo: "etps", id: proc.etp.id, num: proc.etp.numero_gerado, label: "Gerar TR" };
  else if (proc.tr && !proc.matriz && proc.tr.status === "aprovado")
    nextAction = { tipo: "trs", id: proc.tr.id, num: proc.tr.numero_gerado, label: "Gerar Matriz de Riscos" };

  return (
    <div className="bg-[#0F172A] border border-[#334155] rounded-md p-3.5 hover:border-[#FFDF00]/30 transition-colors"
         data-testid={`processo-${proc.demanda.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-[#FFDF00] font-semibold">{proc.demanda.numero_gerado}</span>
            <span className="text-xs text-[#64748B]">·</span>
            <span className="text-xs text-[#94A3B8]">{proc.secretaria || "—"}</span>
            {proc.categoria && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#334155] text-[#94A3B8]">{proc.categoria}</span>
            )}
          </div>
          <div className="text-sm text-[#E9EEF5] mt-1 truncate" title={proc.objeto}>{proc.objeto}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: estagio.color }}>
            {estagio.label}
          </div>
          <div className="text-[10px] text-[#64748B] mt-0.5 font-mono">
            {proc.valor_estimado ? `R$ ${Number(proc.valor_estimado).toLocaleString("pt-BR", {minimumFractionDigits:2,maximumFractionDigits:2})}` : "—"}
          </div>
        </div>
      </div>

      {/* Barra de progresso */}
      <div className="h-1 w-full bg-[#334155] rounded-full overflow-hidden mb-3">
        <div className="h-full transition-all duration-500" style={{ width: `${estagio.pct}%`, background: estagio.color }} />
      </div>

      {/* Steps horizontais */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
        {steps.map((s, i) => (
          <ChainStep key={s.k} step={s} isLast={i === steps.length - 1} />
        ))}
      </div>

      {nextAction ? (
        <div className="mt-3 flex justify-end">
          <button onClick={() => onPromover(nextAction.tipo, nextAction.id, nextAction.num)}
                  className="px-3 py-1.5 rounded bg-gradient-to-r from-[#009C3B] via-[#FFDF00] to-[#002776] text-white text-[11px] font-semibold flex items-center gap-1.5 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`promover-${nextAction.tipo}-${nextAction.id}`}>
            {nextAction.label} <ArrowRight size={11} weight="bold" />
          </button>
        </div>
      ) : proc.estagio !== "no_pca" && (
        <div className="mt-3 text-right">
          <span className="text-[10px] text-[#64748B] italic">
            {proc.estagio === "dfd_pendente"
              ? "Aprove a demanda (DFD) para liberar a geração do ETP"
              : proc.estagio === "dfd_ok" && proc.etp
                ? "Aprove o ETP para liberar a geração do TR"
                : proc.estagio === "etp_ok" && proc.tr
                  ? "Aprove o TR para liberar a Matriz de Riscos"
                  : proc.estagio === "tr_ok" && proc.matriz
                    ? "Promova o TR aprovado ao PCA quando pronto"
                    : "Aprove a etapa anterior para avançar"}
          </span>
        </div>
      )}
    </div>
  );
}

function ChainStep({ step, isLast }) {
  const d = step.data;
  const statusColor = !d ? "#334155" :
    d.status === "aprovado" || d.status === "aprovada" ? "#12B850" :
    d.status === "em_analise" ? "#FFDF00" :
    d.status === "rejeitado" || d.status === "rejeitada" ? "#EF4444" :
    "#94A3B8";
  const bg = !d ? "bg-[#1E293B] border-[#334155] text-[#64748B]" :
    d.status === "aprovado" || d.status === "aprovada" ? "bg-[#009C3B]/15 border-[#009C3B]/40 text-[#12B850]" :
    d.status === "em_analise" ? "bg-[#FFDF00]/10 border-[#FFDF00]/40 text-[#FFDF00]" :
    "bg-[#334155]/40 border-[#334155] text-[#94A3B8]";
  return (
    <>
      {d ? (
        <Link to={step.link} className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] font-mono hover:brightness-125 transition ${bg}`}
              title={d.titulo || d.numero_gerado}>
          <span className="font-sans font-semibold">{step.label}</span>
          <span className="opacity-80">{d.numero_gerado}</span>
        </Link>
      ) : (
        <div className={`flex items-center gap-1 px-2 py-1 rounded border border-dashed text-[10px] ${bg}`}>
          <span className="font-sans">{step.label}</span>
          <span className="opacity-60">—</span>
        </div>
      )}
      {!isLast && <ArrowRight size={10} className="text-[#334155] flex-shrink-0" />}
    </>
  );
}
